import { Injectable, computed, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, tap } from 'rxjs';
import { Compte, CreationCompte, MiseAJourCompte } from '../models/compte.model';
import {
  CreationOperationPlanifiee,
  OperationPlanifiee,
  TableauPrevisionnel,
} from '../models/operation-planifiee.model';
import {
  CreationOperationReelle,
  OperationReelle,
} from '../models/operation-reelle.model';
import { PrevisionMensuelle } from '../models/prevision.model';
import { FiltresRechercheOperations, ResultatRechercheOperations } from '../models/recherche-operation.model';
import { Categorie } from '../models/categorie.model';

export interface OperationImportPowens {
  dateOperation: string;
  dateValeur: string;
  libelle: string;
  categorie: string;
  typeLabel: string;
  statut: string;
  montant: number;
  moyenPaiement: string | null;
  numeroCheque: string | null;
  referenceImport: string;
}

export type ReponseImportPowens =
  | {
      connexionRequise: true;
      urlConnexion: string;
    }
  | {
      connexionRequise: false;
      comptePowens: {
        id: number;
        nom: string;
        solde: number | null;
        devise: string | null;
      };
      comptesCartePowens?: Array<{
        id: number;
        nom: string;
      }>;
      operations: OperationImportPowens[];
    };

export interface ComptePowensDisponible {
  id: number;
  nom: string;
  type: string | null;
  numeroMasque: string | null;
  solde: number | null;
  aVenir: number | null;
  compteParentId: number | null;
}

export interface SelectionComptesPowens {
  comptesDisponibles: ComptePowensDisponible[];
  comptePrincipalId: number | null;
  comptesCarteIds: number[];
}

export interface ModificationSelectionComptesPowens {
  comptePrincipalId: number;
  comptesCarteIds: number[];
}

@Injectable({ providedIn: 'root' })
export class ComptesService {
  private readonly apiUrl = `${environment.apiBaseUrl}/comptes`;
  private readonly powensApiUrl = `${environment.apiBaseUrl}/powens`;

  private comptesSignal = signal<Compte[]>([]);
  readonly comptes = computed(() => this.comptesSignal());

  constructor(private readonly http: HttpClient) {}

  rafraichirComptes(): Observable<Compte[]> {
    return this.http
      .get<Compte[]>(this.apiUrl)
      .pipe(tap((comptes) => this.comptesSignal.set(comptes)));
  }

  creerCompte(payload: CreationCompte): Observable<Compte> {
    return this.http.post<Compte>(this.apiUrl, payload).pipe(
      tap(() => {
        void this.rafraichirComptes().subscribe();
      })
    );
  }

  mettreAJourCompte(compteId: number, payload: MiseAJourCompte): Observable<Compte> {
    return this.http
      .patch<Compte>(`${this.apiUrl}/${compteId}`, payload)
      .pipe(
        tap(() => {
          void this.rafraichirComptes().subscribe();
        })
      );
  }

  mettreAJourSoldeCompte(compteId: number, soldeActuel: number): Observable<Compte> {
    return this.mettreAJourCompte(compteId, { soldeActuel });
  }

  mettreAJourSoldeCBDiffere(compteId: number, soldeCBDiffere: number): Observable<Compte> {
    return this.mettreAJourCompte(compteId, { soldeCBDiffere });
  }

  listerOperationsPlanifiees(
    compteId: number,
    annee: number,
    mois: number
  ): Observable<OperationPlanifiee[]> {
    const params = new HttpParams()
      .set('annee', annee.toString())
      .set('mois', mois.toString());

    return this.http.get<OperationPlanifiee[]>(
      `${this.apiUrl}/${compteId}/operations-planifiees`,
      { params }
    );
  }

  definirOverrideMois(
    operationId: number,
    annee: number,
    mois: number,
    montant: number
  ): Observable<{ id: number; annee: number; mois: number; montant: number }> {
    return this.http.put<{ id: number; annee: number; mois: number; montant: number }>(
      `${this.apiUrl}/operations-planifiees/${operationId}/override`,
      { annee, mois, montant }
    );
  }

  supprimerOverrideMois(
    operationId: number,
    annee: number,
    mois: number
  ): Observable<void> {
    const params = new HttpParams()
      .set('annee', annee.toString())
      .set('mois', mois.toString());
    return this.http.delete<void>(
      `${this.apiUrl}/operations-planifiees/${operationId}/override`,
      { params }
    );
  }

  listerOperationsTableau(
    compteId: number,
    annee: number,
    moisDebut: number,
    nombreMois: number = 6
  ): Observable<TableauPrevisionnel> {
    const params = new HttpParams()
      .set('annee', annee.toString())
      .set('moisDebut', moisDebut.toString())
      .set('nombreMois', nombreMois.toString());

    return this.http.get<TableauPrevisionnel>(
      `${this.apiUrl}/${compteId}/operations-planifiees/tableau`,
      { params }
    );
  }

  creerOperationPlanifiee(
    compteId: number,
    payload: CreationOperationPlanifiee
  ): Observable<OperationPlanifiee> {
    return this.http.post<OperationPlanifiee>(
      `${this.apiUrl}/${compteId}/operations-planifiees`,
      payload
    );
  }

  mettreAJourOperationPlanifiee(
    operationId: number,
    payload: Partial<CreationOperationPlanifiee>
  ): Observable<OperationPlanifiee> {
    return this.http.put<OperationPlanifiee>(
      `${this.apiUrl}/operations-planifiees/${operationId}`,
      payload
    );
  }

  supprimerOperationPlanifiee(operationId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/operations-planifiees/${operationId}`
    );
  }

  supprimerGroupePlanifiees(groupeRef: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/operations-planifiees/groupe/${encodeURIComponent(groupeRef)}`
    );
  }

  supprimerMultiplePlanifiees(ids: number[]): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/operations-planifiees/multi`,
      { body: { ids } }
    );
  }

  rechercherOperations(
    filtres: FiltresRechercheOperations, page = 1,
  ): Observable<ResultatRechercheOperations> {
    let params = new HttpParams().set('page', page.toString());
    for (const [nom, valeur] of Object.entries(filtres)) {
      if (valeur !== undefined && valeur !== null && valeur !== '') {
        params = params.set(nom, String(valeur));
      }
    }
    return this.http.get<ResultatRechercheOperations>(
      `${this.apiUrl}/operations-reelles/recherche`, { params },
    );
  }

  listerOperationsReelles(
    compteId: number,
    annee: number,
    mois: number
  ): Observable<OperationReelle[]> {
    const params = new HttpParams()
      .set('annee', annee.toString())
      .set('mois', mois.toString());

    return this.http.get<OperationReelle[]>(
      `${this.apiUrl}/${compteId}/operations-reelles`,
      { params }
    );
  }

  importerOperationsPowens(
    compteId: number,
    annee: number,
    mois: number
  ): Observable<ReponseImportPowens> {
    const params = new HttpParams()
      .set('annee', annee.toString())
      .set('mois', mois.toString());

    return this.http.get<ReponseImportPowens>(
      `${this.powensApiUrl}/comptes/${compteId}/operations`,
      { params }
    );
  }

  ouvrirGestionPowens(): Observable<{ urlGestion: string }> {
    return this.http.get<{ urlGestion: string }>(
      `${this.powensApiUrl}/gestion`,
    );
  }

  obtenirSelectionComptesPowens(
    compteId: number,
  ): Observable<SelectionComptesPowens> {
    return this.http.get<SelectionComptesPowens>(
      `${this.powensApiUrl}/comptes/${compteId}/selection`,
    );
  }

  enregistrerSelectionComptesPowens(
    compteId: number,
    selection: ModificationSelectionComptesPowens,
  ): Observable<SelectionComptesPowens> {
    return this.http.post<SelectionComptesPowens>(
      `${this.powensApiUrl}/comptes/${compteId}/selection`,
      selection,
    );
  }

  creerOperationReelle(
    compteId: number,
    payload: CreationOperationReelle
  ): Observable<OperationReelle> {
    return this.http.post<OperationReelle>(
      `${this.apiUrl}/${compteId}/operations-reelles`,
      payload
    );
  }

  mettreAJourOperationReelle(
    operationId: number,
    payload: Partial<CreationOperationReelle>
  ): Observable<OperationReelle> {
    return this.http.put<OperationReelle>(
      `${this.apiUrl}/operations-reelles/${operationId}`,
      payload
    );
  }

  supprimerOperationReelle(operationId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/operations-reelles/${operationId}`
    );
  }

  pointerOperationReelle(operationId: number): Observable<{ id: number; estPointee: boolean; misAJourLe: string }> {
    return this.http.patch<{ id: number; estPointee: boolean; misAJourLe: string }>(
      `${this.apiUrl}/operations-reelles/${operationId}/pointer`,
      {}
    );
  }

  depointerOperationReelle(operationId: number): Observable<{ id: number; estPointee: boolean; misAJourLe: string }> {
    return this.http.patch<{ id: number; estPointee: boolean; misAJourLe: string }>(
      `${this.apiUrl}/operations-reelles/${operationId}/depointer`,
      {}
    );
  }

  pointerOperationPlanifiee(
    operationId: number,
    annee: number,
    mois: number,
    options?: {
      dateOperation?: string;
      dateValeur?: string | null;
      montant?: number;
      moyenPaiement?: string | null;
      numeroCheque?: string;
      compteDestinationId?: number | null;
      referenceImport?: string | null;
      libelle?: string;
    }
  ): Observable<OperationReelle> {
    const payload: {
      annee: number;
      mois: number;
      dateOperation?: string;
      dateValeur?: string | null;
      montant?: number;
      moyenPaiement?: string | null;
      numeroCheque?: string;
      compteDestinationId?: number | null;
      referenceImport?: string | null;
      libelle?: string;
    } = {
      annee,
      mois,
    };

    if (options?.dateOperation) {
      payload.dateOperation = options.dateOperation;
    }

    if (options && 'dateValeur' in options) {
      payload.dateValeur = options.dateValeur ?? null;
    }

    if (
      typeof options?.montant === 'number' &&
      !Number.isNaN(options.montant)
    ) {
      payload.montant = options.montant;
    }

    if (options && 'moyenPaiement' in options) {
      payload.moyenPaiement = options.moyenPaiement ?? null;
    }

    if (typeof options?.numeroCheque === 'string') {
      payload.numeroCheque = options.numeroCheque;
    }

    if (
      options &&
      'compteDestinationId' in options &&
      options.compteDestinationId !== undefined
    ) {
      payload.compteDestinationId = options.compteDestinationId;
    }

    if (options?.referenceImport !== undefined) {
      payload.referenceImport = options.referenceImport;
    }

    if (options?.libelle) {
      payload.libelle = options.libelle;
    }

    return this.http.patch<OperationReelle>(
      `${this.apiUrl}/operations-planifiees/${operationId}/pointer`,
      payload
    );
  }

  chargerPrevision(
    compteId: number,
    annee: number,
    mois: number
  ): Observable<PrevisionMensuelle> {
    const params = new HttpParams()
      .set('annee', annee.toString())
      .set('mois', mois.toString());

    return this.http.get<PrevisionMensuelle>(
      `${this.apiUrl}/${compteId}/previsions`,
      { params }
    );
  }

  listerCategories(): Observable<Categorie[]> {
    return this.http.get<Categorie[]>(`${this.apiUrl}/categories`);
  }

  creerCategorie(nom: string): Observable<Categorie> {
    return this.http.post<Categorie>(`${this.apiUrl}/categories`, { nom });
  }
}
